import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import apiClient from '../apis/apiClient';
import './AdminOrders.css';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  useEffect(() => {
    if (activeTab === 'orders') {
      fetchOrders();
    } else {
      fetchCancellations();
    }
  }, [activeTab]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/order');
      setOrders(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCancellations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/cancellations');
      const data = response.data || [];
      setCancellations(data);

      // Mark cancelled orders in orders list
      setOrders(prev =>
        prev.map(order => {
          const isCancelled = data.some(c => c.orderId === order.id);
          return isCancelled ? { ...order, status: 'CANCELLED' } : order;
        })
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const response = await apiClient.patch(`/order/${orderId}/status`, { status: newStatus });
      const updatedOrder = response.data;
      setOrders(prev =>
        prev.map(order => (order.id === updatedOrder.id ? updatedOrder : order))
      );

      if (updatedOrder.status === 'CANCELLED') {
        await fetchCancellations(); // refresh cancellations from backend
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      await apiClient.delete(`/order/${orderId}`);

      setOrders(prev => prev.filter(order => order.id !== orderId));
    } catch (err) {
      setError(err.message);
    }
  };

  const deleteCancellation = async (cancellationId) => {
    try {
      await apiClient.delete(`/cancellations/${cancellationId}`);
      setCancellations(prev => prev.filter(c => c.id !== cancellationId));
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleOrderItems = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';

    let dt;
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)) {
      dt = DateTime.fromFormat(dateString, 'yyyy-MM-dd HH:mm:ss', { zone: 'Europe/Berlin' });
    } else {
      dt = DateTime.fromISO(dateString, { zone: 'Europe/Berlin' });
    }

    if (!dt.isValid) return '-';

    return dt.setZone('Europe/Berlin').toFormat('dd. MMM yyyy, HH:mm');
  };

  if (loading) return <div className="loading">Loading...</div>;

  if (error) {
    return (
      <div className="error-box">
        Error: {error}
        <button onClick={() => setError(null)} className="dismiss-button">Dismiss</button>
      </div>
    );
  }

  return (
    <div className="admin-orders">
      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          Bestellungen
        </button>
        <button
          className={`tab-button ${activeTab === 'cancellations' ? 'active' : ''}`}
          onClick={() => setActiveTab('cancellations')}
        >
          Stornierungen
        </button>
      </div>

      {activeTab === 'orders' ? (
        <>
          <h2>Bestellungen</h2>
          {orders.length === 0 ? (
            <div className="no-orders">No orders found</div>
          ) : (
            <div className="orders-container">
              {orders.map((order) => (
                <div key={order.id} className={`order-card ${order.status === 'CANCELLED' ? 'cancelled-order' : ''}`}>
                  <div className="order-header" onClick={() => toggleOrderItems(order.id)}>
                    <div className="order-summary">
                      <span><strong>Bestell-ID:</strong> #{order.id}</span>
                      <span><strong>Kunde:</strong> {order.name || order.userId || 'Guest'}</span>
                      {(order.phoneNumber || order.shippingAddress?.phoneNumber) && (
                        <span><strong>Tel:</strong> 📞 {order.phoneNumber || order.shippingAddress?.phoneNumber}</span>
                      )}
                      <span><strong>Datum:</strong> {formatDate(order.orderDate)}</span>
                      <span><strong>Gesamt:</strong> €{order.totalAmount?.toFixed(2)}</span>
                    </div>
                    <div className="order-status">
                      <select
                        value={order.status}
                        onChange={(e) => updateOrderStatus(order.id, e.target.value)}
                        className="status-select"
                        onClick={(e) => e.stopPropagation()}
                        disabled={order.status === 'CANCELLED'}
                      >
                        <option value="PENDING">Ausstehend</option>
                        <option value="PLACED">Platziert</option>
                        <option value="PROCESSING">Verarbeitung</option>
                        <option value="SHIPPED">Versendet</option>
                        <option value="DELIVERED">Geliefert</option>
                        <option value="CANCELLED">Abgesagt</option>
                      </select>
                    </div>
                    <button
                      className="delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Are you sure you want to delete order #${order.id}?`)) {
                          deleteOrder(order.id);
                        }
                      }}
                    >
                      Löschen
                    </button>
                  </div>

                  {expandedOrderId === order.id && (
                    <div className="order-details">
                      {(order.shippingAddress || order.phoneNumber || order.name) && (
                        <div className="shipping-address">
                          <h4>Kunden- & Lieferinformationen</h4>
                          {order.name && <p><strong>Name:</strong> {order.name}</p>}
                          {order.email && <p><strong>E-Mail:</strong> {order.email}</p>}
                          {(order.phoneNumber || order.shippingAddress?.phoneNumber) && (
                            <p><strong>Telefonnummer:</strong> 📞 {order.phoneNumber || order.shippingAddress?.phoneNumber}</p>
                          )}
                          {order.shippingAddress && (
                            <>
                              <p><strong>Typ:</strong> {order.shippingAddress.type || 'Lieferung'}</p>
                              <p><strong>Adresse:</strong> {order.shippingAddress.streetAddress}</p>
                              <p><strong>PLZ / Stadt:</strong> {order.shippingAddress.zipCode} {order.shippingAddress.city} {order.shippingAddress.state ? `(${order.shippingAddress.state})` : ''}</p>
                            </>
                          )}
                        </div>
                      )}

                      <h4>Artikel bestellen</h4>
                      {order.orderItems && order.orderItems.length > 0 ? (
                        <table className="order-items-table">
                          <thead>
                            <tr>
                              <th>Artikel</th>
                              <th>Menge</th>
                              <th>Preis</th>
                              <th>Zwischensumme</th>
                            </tr>
                          </thead>
                          <tbody>
                            {order.orderItems.map((item) => (
                              <tr key={item.id}>
                                <td>
                                  <div className="item-info">
                                    <div>
                                      <div className="item-name">{item.itemName}</div>
                                    </div>
                                  </div>
                                </td>
                                <td>{item.quantity}</td>
                                <td>€{item.price?.toFixed(2)}</td>
                                <td>€{(item.price * item.quantity)?.toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div>Für diese Bestellung wurden keine Artikel gefunden</div>
                      )}

                      <div className="order-summary-footer">
                        <div><strong>Gesamtbetrag:</strong> €{order.totalAmount?.toFixed(2)}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          <h2>Stornierungen</h2>
          {cancellations.length === 0 ? (
            <div className="no-cancellations">No cancellations found</div>
          ) : (
            <table className="cancellations-table">
              <thead>
                <tr>
                  <th>Stornierungs-ID</th>
                  <th>Bestell-ID</th>
                  <th>Grund</th>
                  <th>Datum</th>
                  <th>Status</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {cancellations.map((cancellation) => (
                  <tr key={cancellation.id || `${cancellation.orderId}-cancel`}>
                    <td>{cancellation.id || '-'}</td>
                    <td>{cancellation.orderId}</td>
                    <td>{cancellation.reason || 'Cancelled by user'}</td>
                    <td>{formatDate(cancellation.cancelledAt)}</td>
                    <td>{cancellation.status || 'CANCELLED'}</td>
                    <td>
                      <button
                        className="delete-button"
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete cancellation for order #${cancellation.orderId}?`)) {
                            deleteCancellation(cancellation.id);
                          }
                        }}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

export default AdminOrders;
